import { uniq } from "@-/com.npmjs.array-utils-ts";
import {
  getVariationSequenceCodePoints,
  variationSequenceDisplayText,
} from "@-/dev.improve.haniverse.assets.graphemes.fonts";

export const GRAPHEME_LINK_SCHEME = "han-grapheme://";

/**
 * The text to DRAW for a grapheme inside markdown we render ourselves.
 *
 * `Han Composite` carries no cmap format-14 (UVS) subtable, so an authored variation sequence —
 * the left-falling dot `㇔︀` (U+31D4 U+FE00), the rising hook `㇆︁`, the Jigmo2 IVS `𦰩󠄂` — cannot be
 * selected by the shaper and must be rewritten to the private-use code point the font build homed
 * its variant glyph at. Selecting the font family alone is NOT sufficient: the base stroke renders
 * instead, and (because U+FE00 is itself mapped in the merged cmap) the selector draws as its own
 * box glyph beside it. That pair is exactly the "broken drip" the mnemonic badges showed.
 *
 * Mirrors `graphemeDisplayText` (`…grapheme.font`), which the React render paths use. This module
 * is a plain lib with no React, so it calls the script-independent primitive directly rather than
 * the hook — which private-use code point carries a variant is a property of the bundled font, not
 * of simplified/traditional.
 *
 * DISPLAY ONLY. Link URLs and anything round-tripped back into saved markdown keep the authored
 * sequence, which is the corpus lookup key.
 */
function graphemeBadgeText(grapheme: string): string {
  return variationSequenceDisplayText(grapheme, getVariationSequenceCodePoints());
}

// Catalog components also include symbols such as ∐ and ＿, not just Han characters.
const GRAPHEME_REFERENCE_REGEX = /^[^\p{ASCII}\s]\p{Variation_Selector}*$/u;
function mnemonicLinkRegex(): RegExp {
  return /(`{3,}|~{3,})[^\n]*\n[\s\S]*?\1|(`+)[^`]*?\2|(!?)\[((?:\\.|[^\]\\])*)\]\(([^\s)]+)(\s+"[^"]*")?\)/g;
}

// A delimiter run (`*`, `_`, `~~`) wrapping non-space text, not preceded by a backslash escape.
// `_` additionally must not sit inside a word (`snake_case` is literal in CommonMark).
function inlineEmphasisRegex(): RegExp {
  return /(?<!\\)(?:(\*{1,3}|~~)(?=\S)(.*?\S)(?<!\\)\1|(?<![\p{L}\p{N}])(_{1,3})(?=\S)(.*?\S)(?<!\\)\3(?![\p{L}\p{N}]))/gu;
}

/** Strips inline emphasis and code markers from a link label, keeping backslash escapes intact. */
function stripInlineMarkdown(label: string): string {
  let text = label.replaceAll(/(?<!\\)(`+)(.+?)\1/g, (_match, _ticks, code: string) => code.trim());
  // Nested emphasis (`**_sun_**`) unwraps one layer per pass.
  for (;;) {
    const next = text.replaceAll(
      inlineEmphasisRegex(),
      (_match, _mark, markedText: string | undefined, _underscores, underscoredText) =>
        markedText ?? underscoredText,
    );
    if (next === text) {
      return text;
    }
    text = next;
  }
}

/**
 * The authored term for a component as PLAIN text, excluding images, code and unrelated links.
 * Callers render it in a single styled `Text`, so inline markdown (`[_stick_](丨)`) is stripped
 * rather than shown literally.
 */
export function getMnemonicGraphemeLabel(content: string, grapheme: string): string | undefined {
  for (const match of content.matchAll(mnemonicLinkRegex())) {
    const [, fence, code, image, label, url] = match;
    if (fence || code || image || label === undefined || url === undefined) {
      continue;
    }
    let reference: string;
    try {
      reference = parseGraphemeFromDescriptionLinkUrl(url) ?? decodeURIComponent(url);
    } catch {
      continue;
    }
    if (reference !== grapheme) {
      continue;
    }
    const text = label.endsWith(` ${grapheme}`) ? label.slice(0, -grapheme.length - 1) : label;
    const plainText = stripInlineMarkdown(text)
      .replaceAll(/\\([\\`*{}[\]()#+\-.!_>~])/g, "$1")
      .trim();
    if (plainText !== "") {
      return plainText;
    }
  }
  return undefined;
}

/** Display-only decoration: keep saved/editor markdown free of badges and layout characters. */
export function addMnemonicGlyphBadges(
  content: string,
  currentGrapheme?: string,
  glyphDecoration: "code-badge" | "outside-link" = "code-badge",
): string {
  return content.replaceAll(
    mnemonicLinkRegex(),
    (match, fence, code, image, label: string, url: string, title: string | undefined = "") => {
      if (fence || code || image) {
        return match;
      }
      let grapheme: string | null;
      try {
        const reference = decodeURIComponent(url);
        if (reference === ".") {
          const text =
            currentGrapheme && label.endsWith(` ${currentGrapheme}`)
              ? label.slice(0, -currentGrapheme.length - 1)
              : label;
          // The file already bolds the gloss (`[**bright**](.)`); only the link is dropped.
          return text;
        }
        grapheme =
          parseGraphemeFromDescriptionLinkUrl(url) ??
          (GRAPHEME_REFERENCE_REGEX.test(reference) ? reference : null);
      } catch {
        return match;
      }
      if (!grapheme) {
        return match;
      }
      const linkUrl = url.startsWith(GRAPHEME_LINK_SCHEME)
        ? url
        : `${GRAPHEME_LINK_SCHEME}${encodeURIComponent(grapheme)}`;
      // Older descriptions already appended the glyph as plain text inside the link.
      const text = label.endsWith(` ${grapheme}`) ? label.slice(0, -grapheme.length - 1) : label;
      const nonbreakingText = text.replaceAll(/\s+/g, "\u00A0").replaceAll(/[-/]/g, "$&\u2060");
      // The code span gives the glyph a native rounded background and normally remains inside the
      // same link. Android's native Markdown renderer loses Han Composite anywhere inside a link,
      // even without the nested code span. Android callers therefore keep the term as the link and
      // place the glyph immediately after it, in the paragraph's verified font run. NBSP keeps the
      // decoration attached to the linked term while still ending the Latin shaping cluster. A
      // WORD JOINER here makes Android shape the glyph with the linked Latin run and produces the
      // same LastResort box this branch exists to avoid. No joiner may sit directly after the label
      // inside the link either: a closing `_` followed by U+2060 (neither whitespace nor
      // punctuation) is intraword, so `[_stick_](丨)` would render its underscores literally.
      // The badge draws `graphemeBadgeText`, not the authored grapheme: `linkUrl` above already
      // captured the authored sequence, so the preview destination stays keyed on the real glyph.
      const displayGrapheme = graphemeBadgeText(grapheme);
      if (glyphDecoration === "outside-link") {
        return `[${nonbreakingText}](${linkUrl}${title ?? ""})\u00A0${displayGrapheme}`;
      }
      return `[${nonbreakingText}\u00A0\u2060\`\u00A0${displayGrapheme}\u00A0\`](${linkUrl}${title ?? ""})`;
    },
  );
}

export function collectBracketTexts(content: string): string[] {
  const textList: string[] = [];
  for (const match of content.matchAll(/(!?)\[([^\]\n]+)\](\()?/g)) {
    const [, bang, text, open] = match;
    if (bang === "!" || open === "(") {
      continue;
    }
    if (text === undefined) {
      continue;
    }
    textList.push(text);
  }
  return uniq(textList);
}

export function preprocessBracketLinks(content: string, backToFront: Map<string, string>): string {
  return content.replaceAll(
    /(!?)\[([^\]\n]+)\](\()?/g,
    (match, bang: string, text: string, open) => {
      if (bang === "!" || open === "(") {
        return match;
      }
      const front = backToFront.get(text);
      if (front === undefined) {
        return match;
      }
      // Same split as the badge above: the label DRAWS the rewritten glyph, the URL keeps the
      // authored sequence so the link still resolves to the right card.
      return `[${text} ${graphemeBadgeText(front)}](${GRAPHEME_LINK_SCHEME}${encodeURIComponent(front)})`;
    },
  );
}

export function parseGraphemeFromDescriptionLinkUrl(url: string): string | null {
  if (!url.startsWith(GRAPHEME_LINK_SCHEME)) {
    return null;
  }
  const grapheme = decodeURIComponent(url.slice(GRAPHEME_LINK_SCHEME.length));
  return grapheme.length === 0 ? null : grapheme;
}
